import type { ISODateTimeString } from '@gat-ship/types'
import { isISODateTimeString } from '@gat-ship/types'
import { Temporal, Intl } from '@js-temporal/polyfill'
import { ref } from 'vue'

type DateTimeFormatOptions = globalThis.Intl.DateTimeFormatOptions

export const currentTimeZone = ref(
  Intl.DateTimeFormat().resolvedOptions().timeZone
)

export function convertTimestampToISODateTimeString(
  timestamp: number
): ISODateTimeString {
  return Temporal.Instant.from(
    new Date(timestamp).toISOString()
  ).toString() as ISODateTimeString
}

export function setCurrentTimeZone(timeZone: string) {
  currentTimeZone.value = timeZone
}

export function getCurrentTimeZone() {
  return currentTimeZone.value
}

export const currentLocale = ref(Intl.DateTimeFormat().resolvedOptions().locale)

export function setCurrentLocale(locale: string) {
  currentLocale.value = locale
}

export function getCurrentLocale() {
  return currentLocale.value
}

export function dateForever() {
  return '2099-12-31T00:00:00Z' as ISODateTimeString
}

export function convertInstantToZonedDateTime(
  date: Temporal.Instant,
  timeZone?: string
) {
  const _timeZone = timeZone ?? currentTimeZone.value
  return date.toZonedDateTimeISO(_timeZone)
}

export function convertISODateTimeStringToZonedDateTime(
  date: ISODateTimeString,
  timeZone?: string
) {
  const _timeZone = timeZone ?? currentTimeZone.value
  return Temporal.Instant.from(date).toZonedDateTimeISO(_timeZone)
}

export function getDurationBetweenISODateTimeStrings(
  date1: ISODateTimeString,
  date2: ISODateTimeString
) {
  const _date1 = convertISODateTimeStringToZonedDateTime(date1)
  const _date2 = convertISODateTimeStringToZonedDateTime(date2)
  return _date1.since(_date2)
}

export function dateNow(timeZone?: string) {
  const _timeZone = timeZone ?? currentTimeZone.value

  return Temporal.Now.zonedDateTimeISO(_timeZone)
    .toInstant()
    .toString() as ISODateTimeString
}

export function format(
  date: ISODateTimeString,
  options?: {
    timeZone?: string
    locale?: string
    timeStyle?: 'short' | 'long' | 'full' | 'medium'
    dateStyle?: 'short' | 'long' | 'full' | 'medium'
    dateOnly?: boolean
    day?: '2-digit' | 'numeric'
    month?: '2-digit' | 'numeric' | 'short' | 'long' | 'narrow'
    year?: '2-digit' | 'numeric'
  }
) {
  const _timeZone = options?.timeZone ?? currentTimeZone.value
  // Use English locale but keep timezone for local time formatting
  const _locale = options?.locale ?? 'en'

  return Temporal.Instant.from(date)
    .toZonedDateTimeISO(_timeZone)
    .toLocaleString(_locale, options)
}

export function formatToShortDate(date: ISODateTimeString) {
  return format(date, {
    day: '2-digit',
    month: 'short'
  })
    .split(' ')
    .reverse()
    .join(' ')
}

export function formatToDateTimeString(
  date: ISODateTimeString,
  format?: 'short' | 'full' | 'long' | 'medium',
  options?: { hideYearIfCurrent?: boolean }
): string {
  if (!isISODateTimeString(date)) {
    return date
  }
  const resolvedFormat: 'short' | 'medium' | 'long' | 'full' =
    format ?? 'medium'
  const reslovedOptions = options ?? { hideYearIfCurrent: true }
  const timeZone = currentTimeZone.value
  const zonedDate = Temporal.Instant.from(date).toZonedDateTimeISO(timeZone)
  const now = Temporal.Now.zonedDateTimeISO(timeZone)

  const isCurrentYear =
    reslovedOptions.hideYearIfCurrent && zonedDate.year === now.year

  const dateOptions: DateTimeFormatOptions = isCurrentYear
    ? (() => {
        // Hide the year by explicitly setting day/month and omitting year.
        switch (resolvedFormat) {
          case 'long':
          case 'full':
            return {
              day: '2-digit' as const,
              month: 'long' as const
            } satisfies DateTimeFormatOptions
          case 'short':
          case 'medium':
          default:
            return {
              day: '2-digit' as const,
              month: 'short' as const
            } satisfies DateTimeFormatOptions
        }
      })()
    : ({ dateStyle: resolvedFormat } satisfies DateTimeFormatOptions)

  const timeOptions: DateTimeFormatOptions = isCurrentYear
    ? ({
        hour: '2-digit',
        minute: '2-digit'
      } satisfies DateTimeFormatOptions)
    : ({ timeStyle: 'short' } satisfies DateTimeFormatOptions)

  return zonedDate
    .toLocaleString(currentLocale.value, {
      hour12: false,
      ...dateOptions,
      ...timeOptions
    })
    .toString()
    .replace(',', '')
}

export function formatToTimeString(
  date: ISODateTimeString,
  format?: 'short' | 'full' | 'long' | 'medium'
): string {
  if (!isISODateTimeString(date)) {
    return date
  }
  return Temporal.Instant.from(date).toLocaleString(
    Intl.DateTimeFormat().resolvedOptions().locale,
    {
      hour12: false,
      dateStyle: undefined,
      timeStyle: format ?? 'short'
    }
  )
}

export function timeRemaining(
  isoDate: ISODateTimeString,
  largestUnit:
    | 'years'
    | 'months'
    | 'days'
    | 'hours'
    | 'minutes'
    | 'seconds' = 'days'
) {
  const timeZone = Temporal.Now.timeZoneId() // Get current system time zone
  const now = Temporal.Now.zonedDateTimeISO(timeZone)
  const past = Temporal.Instant.from(isoDate).toZonedDateTimeISO(timeZone)
  return past.since(now, { largestUnit })
}

export function timeSince(
  isoDate: ISODateTimeString,
  options?: {
    /** If set to false, the seconds will be shown as 'just now' */
    showSeconds?: boolean
  }
) {
  const timeZone = Temporal.Now.timeZoneId() // Get current system time zone
  const now = Temporal.Now.zonedDateTimeISO(timeZone)
  const past = Temporal.Instant.from(isoDate).toZonedDateTimeISO(timeZone)

  const duration = now.since(past, { largestUnit: 'years' })

  if (duration.years > 0) {
    return `${duration.years} year${duration.years > 1 ? 's' : ''} ago`
  }

  if (duration.months > 0) {
    return `${duration.months} month${duration.months > 1 ? 's' : ''} ago`
  }

  if (duration.days > 0) {
    return `${duration.days} day${duration.days > 1 ? 's' : ''} ago`
  }

  const hoursDuration = now.since(past, { largestUnit: 'hours' })
  if (hoursDuration.hours > 0) {
    return `${hoursDuration.hours} hour${hoursDuration.hours > 1 ? 's' : ''} ago`
  }

  const minutesDuration = now.since(past, { largestUnit: 'minutes' })
  if (minutesDuration.minutes > 0) {
    return `${minutesDuration.minutes} minute${minutesDuration.minutes > 1 ? 's' : ''} ago`
  }
  if (options?.showSeconds ?? true) {
    const secondsDuration = now.since(past, { largestUnit: 'seconds' })
    return `${secondsDuration.seconds} second${secondsDuration.seconds > 1 ? 's' : ''} ago`
  }
  return 'Just now'
}

export { isISODateTimeString }
